import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  CheckCircle, 
  ArrowUp, 
  ChatCircle, 
  Eye, 
  Check,
  Warning,
  Info
} from '@phosphor-icons/react';
import { useDataContext } from '../../contexts/DataContext';
import dataService from '../../services/dataService';
import { useGoogleOAuthStatus } from '../../hooks/useGoogleOAuthStatus';
import { 
  getAccountScopedReminders,
  addAccountScopedReminder,
  updateAccountScopedReminder,
  deleteAccountScopedReminder,
  getEffectiveUserId
} from '../../services/accountScopedDataService';
import { addScopeChangeListener, getCurrentUser } from '../../services/authorizationService';

const Overview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedSources, dataCache, setDataCache, isLoading, setIsLoading } = useDataContext();
  const effectiveUserId = getEffectiveUserId(); // Use account-scoped user ID
  const currentUser = getCurrentUser(); // Get admin's actual UID
  const adminUserId = currentUser?.uid; // Admin's actual UID for OAuth
  const { isConnected: isGoogleConnected, isLoading: isOAuthLoading } = useGoogleOAuthStatus();

  const [reminders, setReminders] = useState([]);
  const [remindersLoading, setRemindersLoading] = useState(true);
  const [remindersLoaded, setRemindersLoaded] = useState(false);
  const [originalReminders, setOriginalReminders] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [latestReview, setLatestReview] = useState(null);
  const [trafficData, setTrafficData] = useState([]);
  const [visibilityScore, setVisibilityScore] = useState(70);
  const [servicesInitialized, setServicesInitialized] = useState(false);
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [showOAuthSuccess, setShowOAuthSuccess] = useState(false);
  const [apiErrors, setApiErrors] = useState([]);

  // Check for OAuth success redirect
  useEffect(() => {
    const connected = searchParams.get('connected');
    if (connected === 'google') {
      setShowOAuthSuccess(true);
      // Clear the URL parameter
      navigate('/dashboard', { replace: true });
      // Auto-hide the success message after 5 seconds
      setTimeout(() => setShowOAuthSuccess(false), 5000);
    }
  }, [searchParams, navigate]);

  // Create a stable key for selected sources to avoid effect thrashing
  const selectionKey = useMemo(() => JSON.stringify({
    analyticsAccountId: selectedSources?.analytics?.accountId || '',
    analyticsPropertyId: selectedSources?.analytics?.propertyId || '',
    searchConsolePropertyUrl: selectedSources?.searchConsole?.propertyUrl || '',
    placesBusinessUrl: selectedSources?.places?.businessUrl || ''
  }), [selectedSources]);

  // Check if any sources are selected
  const areSourcesSelected = useMemo(() => {
    const analyticsPropertyId = selectedSources?.analytics?.propertyId;
    const searchConsolePropertyUrl = selectedSources?.searchConsole?.propertyUrl;
    
    // Only log once when selections change, not on every render
    if (analyticsPropertyId && searchConsolePropertyUrl) {
      return analyticsPropertyId;
    }
    
    return false;
  }, [selectedSources]);

  // Function version for use in callbacks
  const areSourcesSelectedFn = useCallback(() => {
    const analyticsPropertyId = selectedSources?.analytics?.propertyId;
    const searchConsolePropertyUrl = selectedSources?.searchConsole?.propertyUrl;
    
    return !!(analyticsPropertyId && searchConsolePropertyUrl);
  }, [selectedSources]);

  // Note: areAllSourcesSelected function removed as it's no longer needed

  // Simple initialization - no more Google services initialization needed
  useEffect(() => {
    setServicesInitialized(true);
    
    // Debug: Check what's in localStorage
    const savedSelections = localStorage.getItem('selectedDataSources');
    console.log('📦 Saved selections from localStorage:', savedSelections);
    if (savedSelections) {
      try {
        const parsed = JSON.parse(savedSelections);
        console.log('📦 Parsed selections:', parsed);
      } catch (e) {
        console.error('❌ Failed to parse saved selections:', e);
      }
    }
    
    // Debug: Check current selectedSources state
    console.log('🔍 Current selectedSources state:', selectedSources);
  }, [selectedSources]);

  // Load dashboard data when sources are selected
  const loadDashboardData = useCallback(async () => {
    console.log('🔍 loadDashboardData called with:', {
      areSourcesSelected: areSourcesSelectedFn(),
      selectedSources,
      effectiveUserId,
      isGoogleConnected
    });
    
    if (!areSourcesSelectedFn()) {
      console.log('❌ Sources not selected, returning early');
      return;
    }
    
    // Check if Google OAuth is connected before trying to fetch data
    if (!isGoogleConnected) {
      console.log('❌ Google OAuth not connected, skipping data fetch');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      if (!effectiveUserId) {
        console.log('❌ No effectiveUserId, returning early');
        setIsLoading(false);
        return;
      }

      console.log('✅ Starting data fetch for services...');
      
      // Prepare promises for only selected services using new dataService methods
      const promises = [];
      const serviceKeys = [];

      if (selectedSources?.analytics?.propertyId) {
        // Use the propertyId for Analytics data fetching
        const propertyId = selectedSources.analytics.propertyId;
        // Use Firebase account ID for OAuth token lookup, not Google Analytics account ID
        const firebaseAccountId = currentUser?.accountId || effectiveUserId;
        console.log('📊 Adding Analytics service with propertyId:', propertyId, 'firebaseAccountId (for OAuth):', firebaseAccountId);
        // Use admin's UID for OAuth, Firebase account ID for scoping (NOT Google Analytics account ID)
        promises.push(dataService.refreshData(adminUserId, 'analytics', propertyId, firebaseAccountId));
        serviceKeys.push('analytics');
      } else {
        console.log('❌ Analytics not selected or missing propertyId:', selectedSources?.analytics);
      }
      
      if (selectedSources?.searchConsole?.propertyUrl) {
        console.log('🔍 Adding Search Console service with URL:', selectedSources.searchConsole.propertyUrl);
        // Use Firebase account ID for OAuth token lookup, not Google Analytics account ID
        const firebaseAccountId = currentUser?.accountId || effectiveUserId;
        // Use admin's UID for OAuth, client's account ID for scoping
        promises.push(dataService.refreshData(adminUserId, 'searchConsole', selectedSources.searchConsole.propertyUrl, firebaseAccountId));
        serviceKeys.push('searchConsole');
      } else {
        console.log('❌ Search Console not selected or missing propertyUrl:', selectedSources?.searchConsole);
      }
      
      if (selectedSources?.places?.businessUrl) {
        console.log('📍 Adding Places service with URL:', selectedSources.places.businessUrl);
        // Pass effectiveUserId as first parameter per dataService signature
        promises.push(
          dataService.getPlacesData(
            effectiveUserId,
            selectedSources.places.businessUrl
          )
        );
        serviceKeys.push('places');
      } else {
        console.log('❌ Places not selected or missing businessUrl:', selectedSources?.places);
      }

      console.log('🚀 Prepared promises for services:', serviceKeys);
      console.log('📝 Total promises:', promises.length);

      // Load data for selected services
      const results = await Promise.allSettled(promises);
      console.log('📊 Data fetch results:', results);

      // Build updates and use functional state update to avoid dataCache dependency churn
      const updates = {};
      const errors = [];
      results.forEach((result, index) => {
        const serviceKey = serviceKeys[index];
        console.log(`📝 Processing result for ${serviceKey}:`, result);
        if (result.status === 'fulfilled') {
          console.log(`✅ ${serviceKey} data loaded successfully:`, result.value);
          updates[serviceKey] = result.value;
          
          // Check if the data contains an error (from fallback/API error)
          if (result.value?.error) {
            errors.push({
              service: serviceKey,
              error: result.value.error,
              isFallback: result.value.isFallback
            });
          }
        } else {
          console.error(`❌ Failed to load ${serviceKey} data:`, result.reason);
          errors.push({
            service: serviceKey,
            error: {
              type: 'fetch_error',
              message: result.reason?.message || 'Failed to fetch data',
              details: 'Network or service error occurred'
            },
            isFallback: false
          });
        }
      });
      
      // Update API errors state
      setApiErrors(errors);
      
      console.log('🔄 Final updates to apply:', updates);
      
      setDataCache(prev => ({
        ...prev,
        ...updates,
        lastUpdated: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSources, areSourcesSelectedFn, effectiveUserId, adminUserId, currentUser?.accountId, setIsLoading, setDataCache, isGoogleConnected]);

  // Load dashboard data when sources are selected
  useEffect(() => {
    if (!effectiveUserId) return;
    if (!areSourcesSelected) return;
    if (isOAuthLoading) return; // Wait for OAuth status to be determined
    if (!isGoogleConnected) return; // Don't try to load data if not connected
    
    const timer = setTimeout(() => {
      loadDashboardData();
    }, 100);
    return () => clearTimeout(timer);
  }, [selectionKey, effectiveUserId, loadDashboardData, areSourcesSelected, selectedSources, isOAuthLoading, isGoogleConnected]);

  // Trigger data loading when OAuth status changes to connected
  useEffect(() => {
    if (isGoogleConnected && !isOAuthLoading && areSourcesSelectedFn() && effectiveUserId) {
      console.log('OAuth connected, triggering data load');
      loadDashboardData();
    }
  }, [isGoogleConnected, isOAuthLoading, areSourcesSelectedFn, effectiveUserId, loadDashboardData]);

  // Load saved reminders from Firestore on component mount
  useEffect(() => {
    const loadReminders = async () => {
      if (!effectiveUserId) {
        setRemindersLoading(false);
        return;
      }
      
      try {
        const reminders = await getAccountScopedReminders();
        
        // Only set reminders if we actually got some from Firestore
        if (reminders && reminders.length > 0) {
          // Remove any duplicate reminders that might exist in Firestore
          const uniqueReminders = reminders.filter((reminder, index, self) => 
            index === self.findIndex(r => r.id === reminder.id)
          );
          
          setReminders(uniqueReminders);
          setOriginalReminders([...uniqueReminders]); // Store original reminders
        } else {
          setReminders([]);
          setOriginalReminders([]); // Ensure originalReminders is empty if no reminders
        }
        
        setRemindersLoaded(true); // Set flag to true after loading reminders
        
      } catch (error) {
        console.error('Error loading reminders from Firestore:', error);
        setReminders([]);
        setOriginalReminders([]);
        setRemindersLoaded(true);
      } finally {
        setRemindersLoading(false);
      }
    };
    
    if (effectiveUserId) {
      loadReminders();
    } else {
      setRemindersLoading(false);
    }
  }, [effectiveUserId]);

  // Listen for account scope changes
  useEffect(() => {
    const unsubscribe = addScopeChangeListener((scopeData) => {
      console.log('Account scope changed in Overview:', scopeData);
      // Reload data when scope changes
      if (scopeData.effectiveAccountId && areSourcesSelectedFn()) {
        loadDashboardData();
      }
    });

    return unsubscribe;
  }, [loadDashboardData, areSourcesSelectedFn]);

  // Save reminders to Firestore when they change
  useEffect(() => {
    const saveReminders = async () => {
      if (!effectiveUserId) {
        console.log('Skipping save - no effective user ID');
        return;
      }

      if (!remindersLoaded) {
        console.log('Skipping save - reminders not yet loaded from Firestore');
        return;
      }

      const currentString = JSON.stringify(reminders);
      const originalString = JSON.stringify(originalReminders);

      if (currentString === originalString) {
        console.log('No changes in reminders, skipping save.');
        return;
      }

      if (isSaving) {
        console.log('Already saving, skipping this save operation');
        return;
      }

      console.log('Reminders changed, preparing to save...');
      console.log('Current reminders:', reminders);
      console.log('Original reminders:', originalReminders);
      console.log('Changes detected, saving to Firestore...');

      setIsSaving(true);

      try {
        console.log('Saving reminders to Firestore for account:', effectiveUserId, 'reminders:', reminders);
        // Save each reminder individually using account-scoped service
        for (const reminder of reminders) {
          const original = originalReminders.find(r => r.id === reminder.id);
          
          if (!original) {
            // New reminder - shouldn't happen since addReminder handles this
            // But just in case, skip it here
            continue;
          } else if (JSON.stringify(original) !== JSON.stringify(reminder)) {
            // Existing reminder changed - update it
            await updateAccountScopedReminder(reminder.id, reminder);
          }
        }
        
        // Remove deleted reminders
        for (const original of originalReminders) {
          if (!reminders.find(r => r.id === original.id)) {
            await deleteAccountScopedReminder(original.id);
          }
        }
        
        // Update originalReminders after successful save
        setOriginalReminders([...reminders]);
        console.log('Reminders saved successfully to Firestore');
      } catch (error) {
        console.error('Error saving reminders to Firestore:', error);
      } finally {
        setIsSaving(false);
      }
    };

    if (effectiveUserId && remindersLoaded) {
      saveReminders();
    }
  }, [reminders, effectiveUserId, remindersLoaded, originalReminders, isSaving]);

  // Removed duplicate effect that could cause repeated reloads

  // Generate latest review from Places data
  useEffect(() => {
    if (dataCache.places?.reviews?.reviews?.length > 0) {
      const review = dataCache.places.reviews.reviews[0];
      const timeAgo = getTimeAgo(new Date(review.createTime));
      
      setLatestReview({
        reviewer: review.reviewer?.displayName || 'Anonymous',
        timeAgo: timeAgo,
        rating: getRatingNumber(review.starRating),
        text: review.comment || 'No comment provided'
      });
    } else {
      setLatestReview(null);
    }
  }, [dataCache.places]);

  // Generate traffic data from Analytics and Search Console
  useEffect(() => {
    const generateTrafficData = () => {
      // Debug: Log the actual data we're working with
      console.log('🔍 Analytics data structure:', {
        hasRows: !!dataCache.analytics?.rows,
        rowCount: dataCache.analytics?.rows?.length || 0,
        hasTotals: !!dataCache.analytics?.totalsForAllResults,
        hasChanges: !!dataCache.analytics?.changes,
        changes: dataCache.analytics?.changes,
        totals: dataCache.analytics?.totalsForAllResults
      });

      console.log('🔍 Search Console data structure:', {
        hasRows: !!dataCache.searchConsole?.rows,
        rowCount: dataCache.searchConsole?.rows?.length || 0,
        hasTotals: !!dataCache.searchConsole?.totals,
        hasChanges: !!dataCache.searchConsole?.changes,
        changes: dataCache.searchConsole?.changes,
        totals: dataCache.searchConsole?.totals
      });

      const data = [];

      // Site Traffic from Analytics
      if (dataCache.analytics?.totalsForAllResults?.totalUsers) {
        // Use the totals object from the Worker for accurate aggregated data
        const totalUsers = dataCache.analytics.totalsForAllResults.totalUsers;
        const userChange = dataCache.analytics.changes?.totalUsers || 0;
        const isPositive = userChange >= 0;
        
        console.log('📊 Using Analytics totals for site traffic:', dataCache.analytics.totalsForAllResults);
        data.push({
          metric: "Site Traffic",
          value: Number(totalUsers).toLocaleString('en-US'),
          change: `${isPositive ? '+' : ''}${userChange.toFixed(2)}%`,
          positive: isPositive
        });
      } else if (dataCache.analytics?.rows) {
        // Fallback to calculating from rows if totals not available
        const totalSessions = dataCache.analytics.rows.reduce((sum, row) => {
          // Handle both old array format and new object format
          if (Array.isArray(row)) {
            return sum + parseInt(row[1] || 0);
          } else if (row && typeof row === 'object') {
            return sum + parseInt(row['ga:sessions'] || 0);
          }
          return sum;
        }, 0);
        console.log('📊 Calculating site traffic from Analytics rows:', {
          rowCount: dataCache.analytics.rows.length,
          totalSessions,
          sampleRows: dataCache.analytics.rows.slice(0, 3)
        });
        data.push({
          metric: "Site Traffic",
          value: Number(totalSessions).toLocaleString('en-US'),
          change: "+11.01%",
          positive: true
        });
      } else {
        console.log('❌ No Analytics data available for site traffic');
        data.push({
          metric: "Site Traffic",
          value: "no data available",
          change: "N/A",
          positive: null
        });
      }

      // Impressions from Search Console
      if (dataCache.searchConsole?.totals?.impressions) {
        // Use the totals object from the Worker for accurate aggregated data
        const totalImpressions = dataCache.searchConsole.totals.impressions;
        const impressionChange = dataCache.searchConsole.changes?.impressions || 0;
        const isPositive = impressionChange >= 0;
        
        console.log('📊 Using Search Console totals for impressions:', dataCache.searchConsole.totals);
        data.push({
          metric: "Impressions",
          value: Number(totalImpressions).toLocaleString('en-US'),
          change: `${isPositive ? '+' : ''}${impressionChange.toFixed(2)}%`,
          positive: isPositive
        });
      } else if (dataCache.searchConsole?.rows) {
        // Fallback to calculating from rows if totals not available
        const totalImpressions = dataCache.searchConsole.rows.reduce((sum, row) => sum + (row.impressions || 0), 0);
        console.log('📊 Calculating impressions from Search Console rows:', {
          rowCount: dataCache.searchConsole.rows.length,
          totalImpressions,
          sampleRows: dataCache.searchConsole.rows.slice(0, 3)
        });
        data.push({
          metric: "Impressions",
          value: Number(totalImpressions).toLocaleString('en-US'),
          change: "-0.03%",
          positive: false
        });
      } else {
        console.log('❌ No Search Console data available for impressions');
        data.push({
          metric: "Impressions",
          value: "no data available",
          change: "N/A",
          positive: null
        });
      }

      // Followers (simulated for now)
      if (dataCache.places?.reviews?.reviews?.length > 0) {
        data.push({
          metric: "Followers",
          value: "156",
          change: "+15.03%",
          positive: true
        });
      } else {
        data.push({
          metric: "Followers",
          value: "No data available",
          change: "N/A",
          positive: null
        });
      }

      // Clicks from Search Console
      if (dataCache.searchConsole?.totals?.clicks) {
        // Use the totals object from the Worker for accurate aggregated data
        const totalClicks = dataCache.searchConsole.totals.clicks;
        const clickChange = dataCache.searchConsole.changes?.clicks || 0;
        const isPositive = clickChange >= 0;
        
        console.log('📊 Using Search Console totals for clicks:', dataCache.searchConsole.totals);
        data.push({
          metric: "Clicks",
          value: Number(totalClicks).toLocaleString('en-US'),
          change: `${isPositive ? '+' : ''}${clickChange.toFixed(2)}%`,
          positive: isPositive
        });
      } else if (dataCache.searchConsole?.rows) {
        // Fallback to calculating from rows if totals not available
        const totalClicks = dataCache.searchConsole.rows.reduce((sum, row) => sum + (row.clicks || 0), 0);
        console.log('📊 Calculating clicks from Search Console rows:', {
          rowCount: dataCache.searchConsole.rows.length,
          totalClicks,
          sampleRows: dataCache.searchConsole.rows.slice(0, 3)
        });
        data.push({
          metric: "Clicks",
          value: Number(totalClicks).toLocaleString('en-US'),
          change: "+6.08%",
          positive: true
        });
      } else {
        console.log('❌ No Search Console data available for clicks');
        data.push({
          metric: "Clicks",
          value: "0",
          change: "No data",
          positive: null
        });
      }

      setTrafficData(data);
    };

    generateTrafficData();
  }, [dataCache]);

  // Calculate visibility score based on data completeness
  useEffect(() => {
    let score = 0;
    if (dataCache.analytics) score += 25;
    if (dataCache.searchConsole) score += 25;
    if (dataCache.places) score += 25;
    if (selectedSources.analytics.accountId) score += 25;
    // Reviews from Places
    if (dataCache.places?.reviews?.reviews?.length > 0) {
      score += 25;
      const avgRating = dataCache.places.reviews.reviews.reduce((sum, review) => sum + (review.rating || 0), 0) / dataCache.places.reviews.reviews.length;
      if (avgRating >= 4.5) score += 15;
      else if (avgRating >= 4.0) score += 10;
      else if (avgRating >= 3.5) score += 5;
    }
    setVisibilityScore(score);
  }, [dataCache, selectedSources]);

  const removeReminder = async (id) => {
    if (!effectiveUserId) return;
    
    // Update local state immediately for better UX
    // The useEffect will handle syncing to Firestore
    setReminders(prev => prev.filter(reminder => reminder.id !== id));
  };

  const toggleReminderComplete = async (id) => {
    if (!effectiveUserId) return;
    
    // Update local state immediately for better UX
    // The useEffect will handle syncing to Firestore
    setReminders(prev => prev.map(reminder => 
      reminder.id === id 
        ? { ...reminder, completed: !reminder.completed }
        : reminder
    ));
  };

  const addReminder = async (text) => {
    if (!effectiveUserId) {
      console.log('No effectiveUserId, cannot add reminder');
      return;
    }
    
    // Check if task with same text already exists
    const existingTask = reminders.find(reminder => 
      reminder.text.toLowerCase().trim() === text.toLowerCase().trim()
    );
    
    if (existingTask) {
      console.log('Task with same text already exists, not adding duplicate');
      setShowAddReminder(false);
      return;
    }
    
    // Generate a unique ID using crypto.randomUUID()
    let uniqueId;
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      uniqueId = crypto.randomUUID();
    } else {
      // Fallback: use timestamp + random number
      uniqueId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    
    const newReminder = { id: uniqueId, text: text, completed: false };
    
    // Update local state immediately for better UX
    setReminders(prev => [...prev, newReminder]);
    setOriginalReminders(prev => [...prev, newReminder]);
    setShowAddReminder(false);
    
    // Then sync with Firestore in the background
    try {
      console.log('Adding reminder to Firestore for account:', effectiveUserId);
      await addAccountScopedReminder(newReminder);
    } catch (error) {
      console.error('Error adding reminder to Firestore:', error);
      // Remove from local state if Firestore add fails
      setReminders(prev => prev.filter(r => r.id !== uniqueId));
      setOriginalReminders(prev => prev.filter(r => r.id !== uniqueId));
      setShowAddReminder(true);
    }
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return "1 Day ago";
    if (diffDays < 7) return `${diffDays} Days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} Weeks ago`;
    return `${Math.floor(diffDays / 30)} Months ago`;
  };

  const getRatingNumber = (rating) => {
    const ratingMap = {
      'ONE': 1, 'TWO': 2, 'THREE': 3, 'FOUR': 4, 'FIVE': 5
    };
    return ratingMap[rating] || 5;
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <svg
        key={index}
        className={`w-4 h-4 ${index < rating ? 'text-yellow-400' : 'text-gray-300'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  // Show connection status
  const showConnectionStatus = () => {
    if (!servicesInitialized) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <Warning className="w-5 h-5 text-yellow-500 mr-2" />
            <span className="text-sm text-yellow-800">Initializing Google services...</span>
          </div>
        </div>
      );
    }

    // Show Google OAuth connection status
    if (!isGoogleConnected) {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Info className="w-5 h-5 text-blue-500 mr-2" />
              <span className="text-sm text-blue-800">
                Connect your Google account to view Analytics, Search Console, and Business Profile data.
              </span>
            </div>
            <button
              onClick={() => navigate('/dashboard/settings')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
            >
              Connect Google
            </button>
          </div>
        </div>
      );
    }

    if (!areSourcesSelected) {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Info className="w-5 h-5 text-blue-500 mr-2" />
              <span className="text-sm text-blue-800">
                Please select your Google Analytics, Search Console, and Business Profile data sources in Account Settings.
              </span>
            </div>
            <button
              onClick={() => navigate('/dashboard/settings')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
            >
              Go to Settings
            </button>
          </div>
        </div>
      );
    }

    // Don't show banner when services are connected and sources are selected
    return null;
  };

  // Note: Removed blocking screens for OAuth loading and not connected states.
  // The dashboard will now show regardless of connection status, with appropriate alerts/messages inline.


  return (
    <div className="p-6 space-y-6">
      {/* Header */}
        <div>
        <h1 className="text-3xl font-bold text-gray-900">Overview</h1>
        <p className="text-gray-600 mt-2">Welcome to your healthcare marketing dashboard</p>
      </div>

      {/* OAuth Success Message */}
      {showOAuthSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            <span className="text-sm text-green-800">
              <strong>Google account connected successfully!</strong> You can now configure your services in Account Settings.
            </span>
          </div>
        </div>
      )}

      {/* API Error Messages */}
      {apiErrors.length > 0 && (
        <div className="mb-6 space-y-3">
          {apiErrors.map((errorInfo, index) => {
            const serviceName = errorInfo.service === 'analytics' ? 'Google Analytics' : 
                               errorInfo.service === 'searchConsole' ? 'Google Search Console' : 
                               errorInfo.service === 'places' ? 'Google Places' : errorInfo.service;
            
            return (
              <div key={index} className={`border rounded-lg p-4 ${
                errorInfo.isFallback 
                  ? 'bg-yellow-50 border-yellow-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start">
                  <Warning className={`w-5 h-5 mr-3 mt-0.5 ${
                    errorInfo.isFallback ? 'text-yellow-600' : 'text-red-600'
                  }`} />
                  <div className="flex-1">
                    <h4 className={`font-medium ${
                      errorInfo.isFallback ? 'text-yellow-800' : 'text-red-800'
                    }`}>
                      {errorInfo.isFallback ? 'Using Cached Data' : 'Service Unavailable'}
                    </h4>
                    <p className={`text-sm mt-1 ${
                      errorInfo.isFallback ? 'text-yellow-700' : 'text-red-700'
                    }`}>
                      {errorInfo.isFallback 
                        ? `${serviceName} is temporarily unavailable. Showing cached data from ${new Date().toLocaleDateString()}.`
                        : `${serviceName} is currently unavailable. ${errorInfo.error.details || 'Please try again later.'}`
                      }
                    </p>
                    {errorInfo.error.status && (
                      <p className={`text-xs mt-1 ${
                        errorInfo.isFallback ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        Error {errorInfo.error.status}: {errorInfo.error.message}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setApiErrors(prev => prev.filter((_, i) => i !== index))}
                    className={`ml-3 text-sm ${
                      errorInfo.isFallback ? 'text-yellow-600 hover:text-yellow-800' : 'text-red-600 hover:text-red-800'
                    }`}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
        
      {/* Connection Status Alert - Above all blocks */}
      {showConnectionStatus()}

      {/* Main Banner and Registration Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Banner */}
        <div className="lg:col-span-2 flex items-center bg-gradient-to-r from-vision to-purple-700 rounded-xl p-8 text-white relative overflow-hidden">
          {/* Background SVG */}
          <div className="absolute top-5 right-10 w-64 h-64 opacity-40 pointer-events-none">
            <svg className="w-full h-full" viewBox="0 0 277 276" fill="none">
              <path d="M0.157201 98.1912C-0.953603 82.0717 3.91685 64.9872 11.7779 50.9962C29.4653 19.6085 59.0013 4.11329 94.7179 1.84293C130.435 -0.427426 183.44 -0.824738 217.106 1.92807C247.24 4.39708 267.576 21.595 274.754 50.7407C271.763 96.4884 278.713 147.203 274.782 192.383C273.814 203.536 266.266 220.847 260.541 230.581C244.221 258.393 210.299 275.024 178 275.137V121.519C178 112.097 162.392 98.1628 153.106 98.1628H0.157201V98.1912Z" fill="white" opacity="0.4"></path>
            </svg>
      </div>

          {/* Content */}
          <div className="relative z-10">
            <h2 className="text-4xl mb-3 font-serif max-w-lg">
              Every action you take{' '}
              <span className="text-teal-300 italic">helps your community shift toward health.</span>
            </h2>
            <p className="text-purple-100 mb-6 text-lg">
              Your patients trust doctors, make your leadership visible.
            </p>
            <button
              className="bg-gray-900 text-white px-6 py-3 rounded-full flex items-center space-x-2 hover:bg-gray-800 transition-colors"
              onClick={() => navigate('/dashboard/settings')}
            >
              <svg className="w-5 h-5 mr-2" fill="#3FF1C3" viewBox="0 0 24 24"><path d="M0.0125698 7.85136C-0.07625 6.56244 0.313191 5.19637 0.941761 4.07765C2.35605 1.56789 4.71774 0.328898 7.57364 0.147361C10.4295 -0.0341769 14.6678 -0.0659459 17.3598 0.154168C19.7693 0.35159 21.3954 1.72674 21.9693 4.05722C21.7301 7.71521 22.2858 11.7703 21.9716 15.3829C21.8941 16.2747 21.2906 17.6589 20.8328 18.4373C19.5279 20.6611 16.8154 21.9909 14.2328 21.9999V9.71666C14.2328 8.96328 12.9848 7.84909 12.2424 7.84909H0.0125698V7.85136Z"></path></svg>
              <span>Connect your first account</span>
            </button>
          </div>
        </div>





        {/* Registration Status Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="text-center">
            {/* Compact Header */}
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Achievements</h3>
                <p className="text-sm text-gray-600">{areSourcesSelected ? '2/3' : '1/3'} Unlocked</p>
              </div>
            </div>
            
            {/* Compact Achievement Steps */}
            <div className="space-y-2">
              {/* Certified Achievement */}
              <div className="flex items-center space-x-3 p-2 bg-green-50 rounded-lg border border-green-200">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <span className="font-semibold text-green-800 text-sm">Certified</span>
                </div>
                <span className="text-green-600 text-xs">✓</span>
              </div>
              
              {/* Connected Achievement */}
              <div 
                className={`flex items-center space-x-3 p-2 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
                  areSourcesSelected 
                    ? 'bg-blue-50 border-blue-200' 
                    : 'bg-gray-50 border-gray-200 hover:bg-blue-50 hover:border-blue-200'
                }`}
                onClick={() => {
                  if (!areSourcesSelected) {
                    navigate('/dashboard/settings');
                  }
                }}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                  areSourcesSelected 
                    ? 'bg-blue-500' 
                    : 'bg-gray-300'
                }`}>
                  {areSourcesSelected ? (
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"/>
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                    </svg>
                  )}
                </div>
                <div className="flex-1 text-left">
                  <span className={`font-semibold text-sm ${
                    areSourcesSelected ? 'text-blue-800' : 'text-gray-600'
                  }`}>Connected</span>
                </div>
                {areSourcesSelected ? (
                  <span className="text-blue-600 text-xs">✓</span>
                ) : (
                  <span className="text-blue-600 text-xs">Click to unlock</span>
                )}
          </div>
          
              {/* Promoted Achievement */}
              <div 
                className="flex items-center space-x-3 p-2 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer transition-all duration-200 hover:shadow-md hover:bg-blue-50 hover:border-blue-200"
                onClick={() => navigate('/dashboard/posters')}
              >
                <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <span className="font-semibold text-gray-600 text-sm">Promoted</span>
              </div>
                <span className="text-blue-600 text-xs">Click to unlock</span>
            </div>
          </div>
          
            {/* Compact Progress Bar */}
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${
                    areSourcesSelected 
                      ? 'bg-gradient-to-r from-green-500 to-blue-500 w-2/3' 
                      : 'bg-green-500 w-1/3'
                  }`}
                ></div>
              </div>
            </div>
            
            {/* Quick Action Hint */}
            {!areSourcesSelected && (
              <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-800">
                  <span className="font-semibold">Quick unlock:</span> Click "Connected" to set up Google accounts
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Second Row of Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Reminders Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-teal-500" />
              <h3 className="font-semibold text-gray-900">Reminders</h3>
            </div>
            <button
              onClick={() => setShowAddReminder(true)}
              className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center space-x-1 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add Task</span>
            </button>
            </div>
          
          {remindersLoading ? (
            <div className="text-center py-8 text-gray-500">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-3"></div>
              <p className="text-sm">Loading reminders...</p>
            </div>
          ) : reminders.length > 0 ? (
            <div className="space-y-3">
              {reminders.slice(0, 5).map((reminder) => (
                <div key={reminder.id} className="flex items-center space-x-3 group">
                  <button
                    onClick={() => toggleReminderComplete(reminder.id)}
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                      reminder.completed 
                        ? 'bg-teal-500 border-teal-500 hover:bg-teal-600' 
                        : 'border-gray-300 hover:border-teal-400 hover:bg-teal-50'
                    }`}
                  >
                    {reminder.completed && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </button>
                  <span className={`flex-1 text-sm transition-all duration-200 ${
                    reminder.completed 
                      ? 'text-gray-500 line-through' 
                      : 'text-gray-700'
                  }`}>
                    {reminder.text}
                  </span>
                  <button
                    onClick={() => removeReminder(reminder.id)}
                    className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-all duration-200 p-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
            </div>
              ))}
              {reminders.length > 5 && (
                <button className="text-sm text-teal-600 hover:text-teal-700 mt-2">
                  + {reminders.length - 5} More Tasks
                </button>
              )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-sm">No tasks yet</p>
              <button
                onClick={() => setShowAddReminder(true)}
                className="text-teal-600 hover:text-teal-700 text-sm font-medium mt-2"
              >
                Add your first task
              </button>
          </div>
        )}
      </div>

        {/* Visibility Score Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center space-x-2 mb-4">
            <Eye className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-gray-900">Visibility Score</h3>
            </div>
          
            <div className="text-center">
            <div className="text-4xl font-bold text-purple-600 mb-1">{visibilityScore}</div>
            <div className="text-sm text-gray-600 mb-3">out of 100</div>
            <p className="text-sm text-gray-500">Raise your score by completing tasks</p>
          </div>
        </div>

        {/* Latest Reviews Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center space-x-2 mb-4">
            <ChatCircle className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-gray-900">Latest Reviews</h3>
          </div>
          
          {latestReview ? (
            <div className="relative">
              <button className="absolute top-0 right-0 bg-blue-600 text-white px-3 py-1 rounded-lg text-xs flex items-center space-x-1 hover:bg-blue-700 transition-colors">
                <ChatCircle className="w-3 h-3" />
                <span>Respond</span>
              </button>
              
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-600">
                    {latestReview.reviewer.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{latestReview.reviewer}</div>
                  <div className="text-sm text-gray-500">{latestReview.timeAgo}</div>
                </div>
      </div>

              <div className="flex items-center space-x-1 mb-3">
                {renderStars(latestReview.rating)}
            </div>
              
              <p className="text-sm text-gray-700 leading-relaxed">
                {latestReview.text}
              </p>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No data available
            </div>
          )}
            </div>
      </div>

      {/* Traffic Summary Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">Traffic Summary</h3>
          <button
            onClick={() => {
              console.log('🔄 Manual refresh requested');
              loadDashboardData();
            }}
            disabled={isLoading}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isLoading 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>{isLoading ? 'Refreshing...' : 'Refresh Data'}</span>
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {trafficData.map((item, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="text-sm text-gray-600 mb-2">{item.metric}</div>
              <div className="text-2xl font-bold text-gray-900 mb-2">{item.value}</div>
              {item.positive !== null ? (
                <div className={`flex items-center space-x-1 text-sm ${item.positive ? 'text-green-600' : 'text-red-600'}`}>
                  <ArrowUp className={`w-4 h-4 ${item.positive ? '' : 'rotate-180'}`} />
                  <span>{item.change}</span>
          </div>
        ) : (
                <div className="text-sm text-gray-500">{item.change}</div>
              )}
            </div>
          ))}
          </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard data...</p>
        </div>
      )}

      {/* Last Updated */}
      {dataCache.lastUpdated && (
        <div className="text-center text-sm text-gray-500">
          Last updated: {new Date(dataCache.lastUpdated).toLocaleString()}
        </div>
      )}

      {/* Add Task Modal */}
      {showAddReminder && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Add New Task</h3>
              <button
                onClick={() => setShowAddReminder(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const taskText = formData.get('taskText').trim();
              if (taskText) {
                addReminder(taskText);
              }
            }} className="p-6">
              <div className="mb-4">
                <label htmlFor="taskText" className="block text-sm font-medium text-gray-700 mb-2">
                  Task Description
                </label>
                <textarea
                  id="taskText"
                  name="taskText"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Enter your task description..."
                  autoFocus
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddReminder(false)}
                  className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
                >
                  Add Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;
